// Import the styles first in the order we want
import '@/index.css';
import '@/shared/shadcn/styles.css';

import { initNativeDeepLinks } from '@/app/nativeDeepLinks';
import { installNativeFetchShim } from '@/app/nativeFetch';
import { applyNativePixiPatch } from '@/app/nativePixiPatch';
import { router } from '@/router';
import { queryClient } from '@/shared/api/queryClient';
import { registerServiceWorker } from '@/shared/serviceWorker/registerServiceWorker';
import { setFavicon } from '@/shared/setFavicon';
import '@/shared/utils/chunkErrorHandler';
import { installMobileOomHandler } from '@/shared/utils/mobileOomHandler';
import { initSentry } from '@/shared/utils/sentry';
import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';

// Must run before any module issues a fetch — under the native shell
// the WebView origin (`app://localhost` for Electron) is CORS-rejected
// by every remote API we talk to, so all cross-origin HTTP(S) requests
// are routed through the native HTTP client. No-op outside the native
// shell.
installNativeFetchShim();

// Must run before any `Assets.load*` / `BaseTexture.from` / `Texture.from`
// call — Pixi's URL helpers mishandle the `app://` scheme and mangle
// every asset path otherwise. See `nativePixiPatch.ts` for details.
applyNativePixiPatch();

initSentry();
installMobileOomHandler();
setFavicon();
registerServiceWorker();

// Fire-and-forget: deep-link init is async but the handler may
// immediately replace() the WebView location if the app was launched by
// an OAuth callback, in which case React never mounts here at all.
void initNativeDeepLinks();

// This increases the size of the stack traces that are shown from Rust -> console.error
Error.stackTraceLimit = 100;

const container = document.getElementById('root');
const root = createRoot(container as HTMLElement);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>
);
